import {useEffect, useRef, useState, useCallback} from "react";
import {v4 as uuidv4} from "uuid";
import {get} from "aws-amplify/api";
import awsconfig from "../configs/awsconfig.json";
import {useUser} from "../contexts/AuthContext";

export interface ProgressEvent {
  stage: string;
  progress: number;
  message: string;
  timestamp: string;
}

export interface CompletionEvent {
  reportUrl: string;
  fileName: string;
  timestamp: string;
}

export interface ErrorEvent {
  error: string;
  stage: string;
  timestamp: string;
}

export interface WebSocketMessage {
  type: "progress" | "complete" | "error";
  data: ProgressEvent | CompletionEvent | ErrorEvent;
}

export interface UseWebSocketReturn {
  connectionId: string | null;
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  connect: () => Promise<string | null>;
  disconnect: () => void;
  error: string | null;
  isReconnecting: boolean;
  reconnectAttempts: number;
}

export const useWebSocket = (): UseWebSocketReturn => {
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const {user} = useUser();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef(false);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // Start with 1 second

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    clearHeartbeat();
    // Send ping every 30 seconds to keep connection alive
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({type: "ping"}));
        } catch (err) {
          console.error("Failed to send heartbeat ping:", err);
        }
      }
    }, 30000); // 30 seconds
  }, [clearHeartbeat]);

  const getRealConnectionId = useCallback(async (): Promise<string | null> => {
    // Retry up to 3 times with increasing delay to handle race condition
    // where $connect Lambda hasn't finished writing to DynamoDB yet
    const maxAttempts = 3;
    const delays = [500, 1000, 2000]; // ms

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, delays[attempt - 1]));
          console.log(`Retrying connection ID fetch (attempt ${attempt + 1}/${maxAttempts})`);
        }

        const {body} = await get({
          apiName: "app",
          path: "/websocket/connection-id",
          options: {
            headers: {
              Authorization: user?.jwtToken
            }
          }
        }).response;
        const response = (await body.json()) as {connectionId?: string; userId?: string};
        if (response?.connectionId) {
          return response.connectionId;
        }
      } catch (err) {
        console.error(`Failed to get real WebSocket connection ID (attempt ${attempt + 1}/${maxAttempts}):`, err);
        if (attempt === maxAttempts - 1) {
          return null;
        }
      }
    }
    return null;
  }, [user?.jwtToken]);

  const attemptReconnect = useCallback(() => {
    if (!shouldReconnectRef.current) {
      return;
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      setError("Unable to reconnect to server. Please refresh the page.");
      setIsReconnecting(false);
      return;
    }

    const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts), 30000);
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);

    setIsReconnecting(true);
    clearReconnectTimeout();

    reconnectTimeoutRef.current = setTimeout(() => {
      setReconnectAttempts((prev) => prev + 1);
      connectInternal();
    }, delay);
  }, [reconnectAttempts, maxReconnectAttempts, baseReconnectDelay, clearReconnectTimeout]);

  const connectInternal = useCallback((): Promise<string | null> => {
    return new Promise(async (resolve) => {
      // If there's an existing connection, close it first to ensure fresh connection
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          console.log("Closing existing WebSocket connection before creating new one.");
          wsRef.current.close(1000, "Closing for new connection");
          wsRef.current = null;
          // Small delay to ensure clean closure
          await new Promise((r) => setTimeout(r, 100));
        }
      }

      if (!user) {
        setError("User not authenticated");
        resolve(null);
        return;
      }

      // Clear any previous state before connecting
      setLastMessage(null);
      setError(null);

      // Generate a unique connection ID for this session (temporary)
      const tempConnectionId = uuidv4();

      const wsEndpoint = awsconfig.WebSocket.endpoint;
      const ws = new WebSocket(
        `${wsEndpoint}/?connectionId=${tempConnectionId}&userId=${user.identityId}&token=${user.jwtToken}`
      );

      // Set a timeout for connection attempt
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setError("WebSocket connection timeout");
          resolve(null);
        }
      }, 5000); // 5 second timeout

      ws.onopen = async () => {
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        setError(null);
        setIsReconnecting(false);
        setReconnectAttempts(0); // Reset reconnect attempts on successful connection
        console.log("WebSocket connected, getting real connection ID...");

        // Start heartbeat to keep connection alive
        startHeartbeat();

        // Get the real connection ID from the backend
        const realConnectionId = await getRealConnectionId();
        if (realConnectionId) {
          setConnectionId(realConnectionId);
          console.log("Got real WebSocket connection ID:", realConnectionId);
          resolve(realConnectionId);
        } else {
          console.log("Failed to get real connection ID, using temporary:", tempConnectionId);
          setConnectionId(tempConnectionId);
          resolve(tempConnectionId);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);
          console.log("WebSocket message received:", message);
        } catch (err) {
          console.error("Failed to parse WebSocket message:", event.data);
          setError("Failed to parse message");
        }
      };

      ws.onerror = (event) => {
        clearTimeout(connectionTimeout);
        console.error("WebSocket error:", event);
        setError("WebSocket connection error");
        resolve(null);
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        clearHeartbeat();
        setIsConnected(false);
        setConnectionId(null);

        // Only attempt reconnection if it wasn't a normal closure and we should reconnect
        if (event.code !== 1000 && shouldReconnectRef.current) {
          console.log("WebSocket disconnected unexpectedly, code:", event.code, "- attempting reconnect");
          setError(`Connection lost (code: ${event.code}) - reconnecting...`);
          attemptReconnect();
        } else if (event.code !== 1000) {
          console.log("WebSocket disconnected unexpectedly, code:", event.code);
          setError(`Connection closed unexpectedly (code: ${event.code})`);
        } else {
          console.log("WebSocket disconnected normally");
          setError(null);
        }
      };

      wsRef.current = ws;
    });
  }, [user, getRealConnectionId, startHeartbeat, clearHeartbeat, attemptReconnect]);

  const connect = useCallback((): Promise<string | null> => {
    // Enable auto-reconnect for this connection
    shouldReconnectRef.current = true;
    setReconnectAttempts(0);
    return connectInternal();
  }, [connectInternal]);

  const disconnect = useCallback(() => {
    // Disable auto-reconnect
    shouldReconnectRef.current = false;
    clearReconnectTimeout();
    clearHeartbeat();

    if (wsRef.current) {
      wsRef.current.close(1000, "Manual disconnect");
      wsRef.current = null;
    }
    setIsConnected(false);
    setConnectionId(null);
    setLastMessage(null); // Clear the last message on disconnect
    setError(null);
    setIsReconnecting(false);
    setReconnectAttempts(0);
  }, [clearReconnectTimeout, clearHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      clearReconnectTimeout();
      clearHeartbeat();
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmount");
        wsRef.current = null;
      }
    };
  }, [clearReconnectTimeout, clearHeartbeat]);

  return {
    connectionId,
    isConnected,
    lastMessage,
    connect,
    disconnect,
    error,
    isReconnecting,
    reconnectAttempts
  };
};
